import React, { useState } from 'react';
import { Search, Package, Truck, CheckCircle, Clock, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusSteps = [
  { key: 'confirmed', label: 'Confirmed', icon: CheckCircle },
  { key: 'processing', label: 'Processing', icon: Clock },
  { key: 'shipped', label: 'Shipped', icon: Truck },
  { key: 'delivered', label: 'Delivered', icon: Package },
];

const statusColors = {
  confirmed: 'text-blue-600 bg-blue-100',
  processing: 'text-yellow-600 bg-yellow-100',
  shipped: 'text-purple-600 bg-purple-100',
  delivered: 'text-green-600 bg-green-100',
  cancelled: 'text-red-600 bg-red-100',
  pending: 'text-slate-600 bg-slate-100',
};

const TrackOrder = () => {
  const [formData, setFormData] = useState({ email: '', order_id: '' });
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [searched, setSearched] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setOrder(null);
    setSearched(true);

    try {
      const response = await axios.post(`${API}/orders/track`, formData);
      setOrder(response.data);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Could not find your order. Please check your details.';
      toast({
        title: "Order not found",
        description: msg,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getStepIndex = (status) => {
    const idx = statusSteps.findIndex(s => s.key === status);
    return idx >= 0 ? idx : -1;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="track-order-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-slate-700 text-white py-10 sm:py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold mb-3">Track Your Order</h1>
          <p className="text-base sm:text-lg opacity-90 max-w-xl mx-auto">
            Enter your email and order ID to check the status of your order. No account needed.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Search Form */}
        <Card className="border-2 mb-8" data-testid="track-order-form">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <Search className="h-5 w-5 text-purple-600" />
              Find Your Order
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="track-email">Email Address</Label>
                <Input
                  id="track-email"
                  type="email"
                  placeholder="The email you used when ordering"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={loading}
                  data-testid="track-email-input"
                />
              </div>
              <div>
                <Label htmlFor="track-order-id">Order ID</Label>
                <Input
                  id="track-order-id"
                  type="text"
                  placeholder="e.g. ORD-1234567890"
                  value={formData.order_id}
                  onChange={(e) => setFormData({ ...formData, order_id: e.target.value })}
                  required
                  disabled={loading}
                  data-testid="track-order-id-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                disabled={loading}
                data-testid="track-order-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="mr-2 h-4 w-4" />
                    Track Order
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Order Result */}
        {order && (
          <Card className="border-2" data-testid="track-order-result">
            <CardHeader>
              <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2">
                <CardTitle className="text-lg sm:text-xl">Order {order.order_id}</CardTitle>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium capitalize w-fit ${statusColors[order.status] || statusColors.pending}`}>
                  {order.status === 'cancelled' && <XCircle className="h-4 w-4 mr-1" />}
                  {order.status}
                </span>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Status Progress */}
              {order.status !== 'cancelled' && (
                <div className="relative">
                  <div className="flex justify-between items-center">
                    {statusSteps.map((step, index) => {
                      const active = index <= getStepIndex(order.status);
                      const StepIcon = step.icon;
                      return (
                        <div key={step.key} className="flex flex-col items-center flex-1">
                          <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center ${active ? 'bg-purple-600 text-white' : 'bg-slate-200 text-slate-400'}`}>
                            <StepIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                          </div>
                          <span className={`text-xs mt-1 text-center ${active ? 'text-purple-600 font-medium' : 'text-slate-400'}`}>
                            {step.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  {/* Progress line */}
                  <div className="absolute top-4 sm:top-5 left-[12.5%] right-[12.5%] h-0.5 bg-slate-200 -z-10">
                    <div
                      className="h-full bg-purple-600 transition-all duration-500"
                      style={{ width: `${Math.max(0, getStepIndex(order.status)) / (statusSteps.length - 1) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Order Details */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <h3 className="font-semibold text-slate-800 text-sm">Order Details</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-slate-500">Customer</p>
                    <p className="text-slate-800 font-medium">{order.shipping_info?.firstName} {order.shipping_info?.lastName}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Payment</p>
                    <p className="text-slate-800 font-medium capitalize">{(order.payment_method || '').replace(/_/g, ' ')}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Location</p>
                    <p className="text-slate-800 font-medium">{order.shipping_info?.city}, {order.shipping_info?.state}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Order Date</p>
                    <p className="text-slate-800 font-medium">
                      {order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                  {order.tracking_number && (
                    <div className="col-span-2">
                      <p className="text-slate-500">Tracking Number</p>
                      <p className="text-purple-600 font-medium">{order.tracking_number}</p>
                    </div>
                  )}
                  {order.status_notes && (
                    <div className="col-span-2">
                      <p className="text-slate-500">Notes</p>
                      <p className="text-slate-800">{order.status_notes}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Items */}
              <div>
                <h3 className="font-semibold text-slate-800 text-sm mb-3">Items Ordered</h3>
                <div className="space-y-2">
                  {order.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center bg-white p-3 rounded-lg border text-sm">
                      <div>
                        <p className="font-medium text-slate-800">{item.name}</p>
                        <p className="text-slate-500">Qty: {item.quantity}</p>
                      </div>
                      <p className="font-semibold text-slate-800">${(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center mt-3 pt-3 border-t font-semibold">
                  <span className="text-slate-800">Total</span>
                  <span className="text-purple-600 text-lg">${order.total?.toFixed(2)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* No results message */}
        {searched && !order && !loading && (
          <Card className="border-2 border-dashed" data-testid="track-order-not-found">
            <CardContent className="p-8 text-center">
              <XCircle className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-slate-700 mb-1">Order Not Found</h3>
              <p className="text-slate-500 text-sm">
                We couldn't find an order matching that email and order ID. Please double-check your details and try again.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default TrackOrder;
